import {DialogState} from 'components/Dialogs';
import {useTranslation} from 'react-i18next';
import React from 'react';
import {
	RequirementFromDetailsPage,
	RequirementStatusType,
} from '../requirementDetailsPage.types';
import {RequirementStatus, User} from 'types';

import {
	GetAllRequirementsDocument,
	useUpdateManyRequirementsMutation,
	useUpdateRequirementMutation,
} from '../../hooks/requirements.generated';
import {mapToRef} from 'helpers';
import {useNotificationBar} from 'hooks/useNotificationBar';
import {useNavigate} from 'react-router-dom';
import {useGetAllVexClustersQuery} from 'features/AdminSection/hooks/vexClusters.generated';
import {useGetAllKeywordsQuery} from 'features/AdminSection/hooks/keywords.generated';
import {
	SendApprovalDialog,
	SendApprovalDialogChangeTypeMode,
} from './SendApprovalDialog';
import {ApprovalDialog} from './ApprovalDialog';
import {DeclineDialog} from './DeclineDialog';
import {ListOrDetailsRequirement} from 'features/Requirements/requirements.types';
import {
	RequirementChangeType,
	UpdateRequirementInput,
} from 'types/types.generated';
import {
	RequirementUtilsService,
	CommonRequirementInputFields as Input,
} from 'features/Requirements/RequirementUtils.service';
import {useRequirementsVersions} from 'features/Requirements/hooks/useRequirementsVersions';
import {ConfirmWithoutApproval} from './ConfirmWithoutApproval';
import {ReadyToConfirmDialog} from './ReadyToConfirmDialog';

export interface RegulationFormProps {
	dialogType: RequirementStatusType;
	dialogState: DialogState;
	hideDialog: () => void;
	requirements: RequirementFromDetailsPage[];
}

export const WorkFlowDialogHandler = ({
	dialogType,
	dialogState,
	hideDialog,
	requirements,
}: RegulationFormProps) => {
	const requirementDetails = requirements[0];
	const {t} = useTranslation('features/requirements', {
		keyPrefix: 'WorkflowDialog',
	});

	const navigate = useNavigate();
	const [users, setUsers] = React.useState<User[]>([]);
	const [dialogChangeType, setDialogChangeType] =
		React.useState<RequirementChangeType>(requirementDetails.changeType);
	const {setMessage} = useNotificationBar();
	const [reason, setReason] = React.useState('');
	const {data: vexData} = useGetAllVexClustersQuery({
		skip: dialogState.hidden,
	});
	const {data: keywordsData} = useGetAllKeywordsQuery({
		skip: dialogState.hidden,
	});
	const {getCommonInputFields: mapToInput} = new RequirementUtilsService();

	const [updateRequirementDocument] = useUpdateRequirementMutation();
	const [updateManyRequirementsMutation] = useUpdateManyRequirementsMutation();

	const {getInitialVersion, incrementMinorVersion, incrementMajorVersion} =
		useRequirementsVersions();

	const deDuplicateUsers = (users: User[]): User[] => {
		const seen = new Set<string>();
		return users.filter(user => {
			if (!user?.id) return false;
			if (seen.has(user.id)) return false;
			seen.add(user.id);
			return true;
		});
	};

	const vexClusters = React.useMemo(
		() => vexData?.vexClusters ?? [],
		[vexData],
	);
	const keywords = React.useMemo(
		() => keywordsData?.keywords ?? [],
		[keywordsData],
	);

	const prePopulatedApprovers = React.useMemo((): User[] => {
		const vexClusterRefs = requirementDetails.vexClusters;
		const clusterApprovers = vexClusterRefs?.flatMap(e => {
			const matchedVexCluster = vexClusters?.find(f => [e.id].includes(f.id));
			return matchedVexCluster?.approvers ?? [];
		});
		if (clusterApprovers?.length >= 1)
			return deDuplicateUsers(clusterApprovers);

		const keywordsRef = requirementDetails.keywords;
		const keywordsApprovers = keywordsRef?.flatMap(e => {
			const matchedKeywords = keywords?.find(f => [e.id].includes(f.id));
			return matchedKeywords?.approvers ?? [];
		});
		if (keywordsApprovers?.length >= 1)
			return deDuplicateUsers(keywordsApprovers);
		return [];
	}, [
		vexClusters,
		requirementDetails.vexClusters,
		keywords,
		requirementDetails.keywords,
	]);

	// TODO: Refactor this into property after release
	const updateRequirementDocuments = (
		requirementStatus: RequirementStatus,
		changeType: RequirementChangeType,
		extraWorkflowProps = {},
		approvers?: any,
		version?: string,
		skipModificationDataUpdate?: boolean,
	): Promise<any> => {
		if (requirements.length > 1) {
			return updateManyRequirementsMutation({
				variables: {
					input: {
						ids: requirements.map(r => r.id),
						status: requirementStatus,
						changeType,
						approvalWorkFlow: {
							approverRefs: mapToRef(approvers),
							...extraWorkflowProps,
						},
						skipModificationDataUpdate,
					} as any,
				},
				refetchQueries: [GetAllRequirementsDocument],
			});
		}

		const getInput = (
			info: ListOrDetailsRequirement | any,
		): UpdateRequirementInput => {
			const input: Input = mapToInput(info);
			return {
				...input,
				name: requirementDetails.name,
				id: requirementDetails.id,
				requirementId: requirementDetails.requirementId,
				status: requirementStatus,
				version,
				vexClusterRefs: mapToRef(requirementDetails.vexClusters),
				approvalWorkFlow: {
					approverRefs: mapToRef(approvers),
					...extraWorkflowProps,
				},
				changeType,
				skipModificationDataUpdate,
			};
		};

		return updateRequirementDocument({
			variables: {
				input: getInput(requirementDetails),
			},
			refetchQueries: [GetAllRequirementsDocument],
		});
	};

	const getApprovedVersion = (): string | undefined => {
		if (requirementDetails.version) {
			switch (requirementDetails.changeType) {
				case RequirementChangeType.Content:
					return incrementMajorVersion(requirementDetails.version);
				case RequirementChangeType.Editorial:
					return incrementMinorVersion(requirementDetails.version);
				default:
					return requirementDetails.version || getInitialVersion();
			}
		}

		return getInitialVersion();
	};

	const onSendForApprovalConfirmClick = React.useCallback(async () => {
		await updateRequirementDocuments(
			RequirementStatus.Approval,
			dialogChangeType,
			{
				message: reason,
			},
			mapToRef(
				prePopulatedApprovers?.length > 0 ? prePopulatedApprovers : users,
			),
			requirementDetails.version || undefined,
			true,
		)
			.then(() => {
				setMessage(t('ApprovalSaveMessage'));
				navigate('/');
			})
			.catch(error => {
				console.error('Error:', error);
			});
	}, [
		requirementDetails,
		dialogChangeType,
		users,
		prePopulatedApprovers,
		reason,
	]);

	const getVersionForConfirmWithoutApproval = (): string | undefined => {
		if (requirementDetails.version) {
			switch (dialogChangeType) {
				case RequirementChangeType.Content:
					return incrementMajorVersion(requirementDetails.version);
				case RequirementChangeType.Editorial:
					return incrementMinorVersion(requirementDetails.version);
				default:
					return requirementDetails.version || getInitialVersion();
			}
		}

		return getInitialVersion();
	};

	const onConfirmWithoutApproval = React.useCallback(async () => {
		await updateRequirementDocuments(
			RequirementStatus.Final,
			dialogChangeType,
			{message: reason},
			null,
			getVersionForConfirmWithoutApproval(),
			true,
		)
			.then(() => {
				setMessage(t('ApprovalOKSaveMessage'));
				navigate('/');
			})
			.catch(error => {
				console.error('Error:', error);
			});
	}, [requirementDetails, dialogChangeType, reason]);

	const onConfirmApprovalclick = React.useCallback(async () => {
		await updateRequirementDocuments(
			RequirementStatus.Final,
			RequirementChangeType.None,
			{},
			null,
			getApprovedVersion(),
			true,
		)
			.then(() => {
				setMessage(t('ApprovalOKSaveMessage'));
				navigate('/');
			})
			.catch(error => {
				console.error('Error:', error);
			});
	}, [requirementDetails]);

	const onReadyToConfirmClick = React.useCallback(async () => {
		await updateRequirementDocuments(
			RequirementStatus.ReadyForApproval,
			requirementDetails.changeType,
			{},
			null,
			requirementDetails.version || undefined,
			true,
		)
			.then(() => {
				setMessage(t('ReadyToConfirmSaveMessage'));
				navigate('/');
			})
			.catch(error => {
				console.error('Error:', error);
			});
	}, [requirementDetails]);

	const onDeclineclick = React.useCallback(async () => {
		await updateRequirementDocuments(
			RequirementStatus.Declined,
			RequirementChangeType.None,
			{
				message: reason,
			},
			null,
			requirementDetails.version || undefined,
			true,
		)
			.then(() => {
				setMessage(t('ApprovalNOKSaveMessage'));
				navigate('/');
			})
			.catch(error => {
				console.error('Error:', error);
			});
	}, [requirementDetails, reason]);

	const getChangeTypeMode = (): SendApprovalDialogChangeTypeMode => {
		if (requirementDetails?.status === RequirementStatus.Modified) {
			return SendApprovalDialogChangeTypeMode.Show;
		}
		// eslint-disable-next-line no-warning-comments
		// TODO: Disable Combobox if next major version exists

		return SendApprovalDialogChangeTypeMode.Hide;
	};

	const sendApprovalDialogSubText =
		requirements.length > 1
			? t('ConfirmApprovalTextMultiple')
			: t('ConfirmApprovalText', {name: requirementDetails.name});

	const confirmWithoutApprovalSubText =
		requirements.length > 1
			? t('ConfirmWitoutApprovalTextMultiple')
			: t('ConfirmWitoutApprovalText', {name: requirementDetails.name});

	return (
		<>
			{dialogType === RequirementStatusType.SendToApproval && (
				<SendApprovalDialog
					requirementName={requirementDetails.name}
					subText={sendApprovalDialogSubText}
					defaultApprovers={prePopulatedApprovers}
					{...dialogState}
					onConfirm={onSendForApprovalConfirmClick}
					users={users}
					setUsers={setUsers}
					changeType={dialogChangeType}
					setChangeType={setDialogChangeType}
					changeTypeMode={getChangeTypeMode()}
					reason={reason}
					setReason={setReason}
				/>
			)}
			{dialogType === RequirementStatusType.CompleteWithoutApproval && (
				<ConfirmWithoutApproval
					requirementName={requirementDetails.name}
					subText={confirmWithoutApprovalSubText}
					changeType={dialogChangeType}
					setChangeType={setDialogChangeType}
					changeTypeMode={getChangeTypeMode()}
					reason={reason}
					setReason={setReason}
					{...dialogState}
					onConfirm={onConfirmWithoutApproval}
				/>
			)}

			{dialogType === RequirementStatusType.ReadyToConfirm && (
				<ReadyToConfirmDialog
					requirementName={requirementDetails.name}
					{...dialogState}
					onConfirm={onReadyToConfirmClick}
				/>
			)}

			{dialogType === RequirementStatusType.ClearingOK && (
				<ApprovalDialog
					requirementName={requirementDetails.name}
					{...dialogState}
					onConfirm={onConfirmApprovalclick}
					users={users}
				/>
			)}
			{dialogType === RequirementStatusType.ClearingNOK && (
				<DeclineDialog
					requirementName={requirementDetails.name}
					{...dialogState}
					onConfirm={onDeclineclick}
					reason={reason}
					setReason={setReason}
				/>
			)}
		</>
	);
};
